import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import communityAPI from "../../../api/community";

export default function CertificateVerify() {
  const { certificateId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (certificateId) {
      communityAPI.certificates
        .verify(certificateId)
        .then((res) => {
          if (res.data.valid) {
            setData(res.data);
          } else {
            setError(res.data.message);
          }
        })
        .catch(() => setError("Certificate not found or invalid."))
        .finally(() => setLoading(false));
    }
  }, [certificateId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-white text-lg">Verifying certificate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-10 h-10 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Invalid Certificate
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium">
                Certificate ID: {certificateId}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Badge */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full shadow-2xl mb-4">
            <svg
              className="w-12 h-12 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-slate-600 mb-2">
            Certificate Verified
          </h1>
          <p className="text-blue-500">
            This certificate is authentic and valid
          </p>
        </div>

        {/* Certificate Details Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium mb-1">
                  Certificate Holder
                </p>
                <h2 className="text-3xl font-bold text-white">
                  {data.holder_name}
                </h2>
                <p className="text-blue-200 text-sm mt-1">@{data.username}</p>
              </div>
              <div className="text-right">
                <p className="text-blue-100 text-sm font-medium mb-1">Rank</p>
                <div className="text-4xl font-black text-white">
                  #{data.rank}
                </div>
                <p className="text-blue-200 text-xs">of {data.total_users}</p>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-6 p-8 bg-gray-50">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-1">
                {data.solved_count}
              </div>
              <div className="text-sm text-gray-600 font-medium">
                Challenges Solved
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-600 mb-1">
                {data.total_points}
              </div>
              <div className="text-sm text-gray-600 font-medium">
                Total Points
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-secondary mb-1">
                #{data.rank}
              </div>
              <div className="text-sm text-gray-600 font-medium">
                Leaderboard Rank
              </div>
            </div>
          </div>

          {/* Certificate Info */}
          <div className="px-8 py-6 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">
                  Certificate ID
                </p>
                <p className="text-gray-900 font-mono text-sm bg-gray-100 px-3 py-2 rounded">
                  {data.certificate_id}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">
                  Issue Date
                </p>
                <p className="text-gray-900 font-semibold">
                  {data.issued_date}
                </p>
              </div>
            </div>
          </div>

          {/* Verification Badge */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-8 py-4 border-t border-green-200">
            <div className="flex items-center justify-center gap-3">
              <svg
                className="w-6 h-6 text-green-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-green-800 font-semibold">
                Verified by CTF Platform • Authenticity Guaranteed
              </p>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <p className="text-blue-500 text-sm">
            This certificate has been verified against our secure database and
            is confirmed to be authentic.
          </p>
        </div>
      </div>
    </div>
  );
}
